import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Sparkles, Phone, Copy, Check, Clock, Building2, Crown, XCircle, AlertCircle, CheckCircle2, CreditCard } from 'lucide-react';
import { CATEGORY_META, timeAgo, cn } from '@/lib/utils';
import { toast } from 'sonner';
import { FullPageLoader, EmptyState } from '@/components/Loaders';

function StatusBadge({ status }) {
  const map = {
    trial:    { label: 'Trial',    cls: 'bg-[#F6B24A]/15 text-[#F6B24A] border-[#F6B24A]/30', icon: Sparkles },
    active:   { label: 'Active',   cls: 'bg-green-500/15 text-green-300 border-green-500/30', icon: CheckCircle2 },
    pending:  { label: 'Pending',  cls: 'bg-white/10 text-white/70 border-white/15', icon: Clock },
    expired:  { label: 'Expired',  cls: 'bg-red-500/15 text-red-300 border-red-500/30', icon: AlertCircle },
    rejected: { label: 'Rejected', cls: 'bg-red-500/15 text-red-300 border-red-500/30', icon: XCircle },
    approved: { label: 'Approved', cls: 'bg-green-500/15 text-green-300 border-green-500/30', icon: CheckCircle2 },
    cancelled: { label: 'Cancelled', cls: 'bg-white/10 text-white/60 border-white/10', icon: XCircle },
  };
  const m = map[status] || { label: status, cls: 'bg-white/10 text-white/70 border-white/10', icon: AlertCircle };
  const Icon = m.icon;
  return (
    <Badge className={cn('inline-flex items-center gap-1', m.cls)} data-testid={`subscription-status-${status}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </Badge>
  );
}

export default function SubscriptionPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('local');
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [zelleConfirmation, setZelleConfirmation] = useState('');
  const [zelleSenderName, setZelleSenderName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [payMethod, setPayMethod] = useState('card');

  const isVenueOwner = user?.role === 'venue_owner' || user?.role === 'admin';

  const load = async () => {
    try {
      const [planRes, myRes, payRes] = await Promise.all([
        api.get('/plans'),
        api.get('/subscriptions/me').catch(() => ({ data: [] })),
        api.get('/payments/me').catch(() => ({ data: [] })),
      ]);
      setPlans(planRes.data);
      setItems(myRes.data);
      setPayments(payRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startTrial = async (venueId) => {
    try {
      await api.post('/subscriptions/start-trial', { venue_id: venueId });
      toast.success('30-day free trial activated!');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start trial');
    }
  };

  const openPayDialog = (venue, planId = 'local') => {
    setSelectedVenue(venue);
    setSelectedPlan(planId);
    setZelleConfirmation('');
    setZelleSenderName('');
    setPayMethod('card');
    setPayOpen(true);
  };

  const payWithCard = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/payments/stripe/checkout', {
        venue_id: selectedVenue.id,
        plan: selectedPlan,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start card payment');
      setSubmitting(false);
    }
  };

  const submitPayment = async () => {
    if (!zelleConfirmation.trim() || !zelleSenderName.trim()) {
      toast.error('Confirmation # and sender name required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/payments/submit', {
        venue_id: selectedVenue.id,
        plan: selectedPlan,
        zelle_confirmation: zelleConfirmation.trim(),
        zelle_sender_name: zelleSenderName.trim(),
      });
      toast.success('Payment submitted! Admin will review shortly.');
      setPayOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const copyZelle = async () => {
    try {
      await navigator.clipboard.writeText(plans?.zelle?.recipient || '');
      toast.success('Copied to clipboard');
    } catch {}
  };

  if (loading) return <FullPageLoader />;

  if (!isVenueOwner) {
    return (
      <div className="min-h-screen">
        <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
          <div className="mx-auto flex max-w-2xl items-center gap-2 px-4 py-3">
            <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="subscription-back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>My Subscription</h1>
            <div className="ml-auto"><Logo size={96} withText={false} /></div>
          </div>
        </header>
        <main className="mx-auto max-w-2xl space-y-4 px-4 py-6">
          <EmptyState
            title="You're not listed as a venue owner yet"
            body="Claim your venue first, then come back to pick a plan."
            action={
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                <Button onClick={() => nav('/profile/claim-venue')} className="rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]" data-testid="subscription-claim-cta">
                  Claim a venue
                </Button>
                <Button onClick={() => nav('/pricing')} variant="outline" className="rounded-xl border-white/12 bg-white/5 text-white hover:bg-white/10">
                  View pricing
                </Button>
              </div>
            }
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="subscription-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>My Subscription</h1>
          <Button variant="ghost" onClick={() => nav('/pricing')} className="ml-auto text-[#F6B24A] hover:bg-[#F6B24A]/10" data-testid="subscription-view-plans-link">
            View plans
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-4">
        {items.length === 0 ? (
          <EmptyState title="No venues attached" body="Admin can attach your account to a venue, or claim a venue from the directory." action={
            <Button onClick={() => nav('/profile/claim-venue')} className="rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]">Claim a venue</Button>
          } />
        ) : (
          items.map((it) => (
            <GlassCard key={it.venue?.id || Math.random()} className="p-4" data-testid={`subscription-card-${it.venue?.id}`}>
              <div className="flex items-center gap-3">
                <img
                  src={it.venue?.image_url}
                  onError={(e) => { e.currentTarget.src = '/logo-hero.png'; }}
                  className="h-14 w-20 rounded-xl object-cover"
                  alt=""
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{it.venue?.name}</p>
                  <p className="text-xs text-white/60 truncate">{CATEGORY_META[it.venue?.category]?.label} · {it.venue?.city}, {it.venue?.state}</p>
                </div>
                {it.subscription && <StatusBadge status={it.subscription.status} />}
              </div>

              {it.subscription ? (
                <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="flex items-center gap-2">
                    {it.subscription.plan === 'national' ? <Crown className="h-4 w-4 text-[#2DE2E6]" /> : <Building2 className="h-4 w-4 text-[#F6B24A]" />}
                    <p className="text-sm font-semibold text-white capitalize">{it.subscription.plan} plan</p>
                    <span className="ml-auto text-xs text-white/60" data-testid={`subscription-expiry-${it.venue?.id}`}>
                      Renews {it.subscription.expires_at ? new Date(it.subscription.expires_at).toLocaleDateString() : '—'}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button onClick={() => openPayDialog(it.venue, it.subscription.plan)} className="rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]" data-testid={`subscription-renew-${it.venue?.id}`}>
                      Renew plan
                    </Button>
                    {it.subscription.plan === 'local' && (
                      <Button onClick={() => openPayDialog(it.venue, 'regional')} variant="outline" className="rounded-xl border-[#2DE2E6]/30 bg-[#2DE2E6]/10 text-[#2DE2E6] hover:bg-[#2DE2E6]/15" data-testid={`subscription-upgrade-${it.venue?.id}`}>
                        <Crown className="mr-2 h-4 w-4" /> Upgrade to Regional
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button onClick={() => startTrial(it.venue.id)} className="rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid={`subscription-start-trial-${it.venue?.id}`}>
                    <Sparkles className="mr-2 h-4 w-4" /> Start Free Month (Local)
                  </Button>
                  <Button onClick={() => openPayDialog(it.venue, 'regional')} variant="outline" className="rounded-xl border-[#2DE2E6]/30 bg-[#2DE2E6]/10 text-[#2DE2E6] hover:bg-[#2DE2E6]/15" data-testid={`subscription-go-national-${it.venue?.id}`}>
                    <Crown className="mr-2 h-4 w-4" /> Go Regional from $499/mo
                  </Button>
                </div>
              )}
            </GlassCard>
          ))
        )}

        <section>
          <h2 className="mb-2 text-xs uppercase tracking-[0.18em] text-white/60">Payment history</h2>
          {payments.length === 0 ? (
            <GlassCard className="p-4 text-center text-white/60 text-sm">No payments submitted yet.</GlassCard>
          ) : (
            <GlassCard className="divide-y divide-white/5">
              {payments.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`payment-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-white">${p.amount_usd.toLocaleString()} · <span className="capitalize">{p.plan}</span></p>
                      <StatusBadge status={p.status} />
                    </div>
                    <p className="text-xs text-white/60 truncate">{p.venue_name} · #{p.zelle_confirmation}</p>
                  </div>
                  <p className="text-xs text-white/50">{timeAgo(p.created_at)} ago</p>
                </div>
              ))}
            </GlassCard>
          )}
        </section>
      </main>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md bg-[#0B0C10] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="submit-payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Choose payment method</DialogTitle>
            <DialogDescription className="text-white/60">Pay instantly by card, or send a Zelle transfer for manual review.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl border border-[#F6B24A]/30 bg-[#F6B24A]/10 p-3">
              <p className="text-xs uppercase tracking-wider text-[#F6B24A]">Amount</p>
              <p className="font-display text-2xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }} data-testid="submit-payment-amount">
                ${(plans?.plans?.find((p) => p.id === selectedPlan)?.price_usd || 0).toLocaleString()} / month
              </p>
              <p className="text-xs text-white/70 capitalize">{selectedPlan} plan • {selectedVenue?.name}</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setPayMethod('card')}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors',
                  payMethod === 'card'
                    ? 'border-[#F6B24A] bg-[#F6B24A]/15 text-[#F6B24A]'
                    : 'border-white/12 bg-white/5 text-white/70 hover:bg-white/10'
                )}
                data-testid="pay-method-card-tab"
              >
                <CreditCard className="h-4 w-4" /> Card
              </button>
              <button
                type="button"
                onClick={() => setPayMethod('zelle')}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors',
                  payMethod === 'zelle'
                    ? 'border-[#F6B24A] bg-[#F6B24A]/15 text-[#F6B24A]'
                    : 'border-white/12 bg-white/5 text-white/70 hover:bg-white/10'
                )}
                data-testid="pay-method-zelle-tab"
              >
                <Phone className="h-4 w-4" /> Zelle
              </button>
            </div>

            {payMethod === 'card' ? (
              <div className="space-y-3" data-testid="card-payment-panel">
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <p className="text-sm text-white/80">Pay securely with a credit or debit card. Your subscription activates instantly after payment.</p>
                  <p className="mt-2 text-xs text-white/50">Powered by Stripe. You'll be redirected to a secure checkout page.</p>
                </div>
                <Button onClick={payWithCard} disabled={submitting} className="w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="pay-with-card-button">
                  {submitting ? 'Redirecting…' : (<><CreditCard className="mr-2 h-4 w-4" /> Pay with Card</>)}
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="zelle-payment-panel">
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <p className="text-xs uppercase tracking-wider text-white/60">Zelle to</p>
                  <div className="mt-1 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-white/60" />
                    <span className="font-mono text-base text-white" data-testid="submit-payment-zelle-recipient">{plans?.zelle?.recipient}</span>
                    <Button variant="ghost" size="icon" onClick={copyZelle} className="ml-auto text-white/70 hover:bg-white/10" data-testid="submit-payment-copy-recipient">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-white/60">Recipient: {plans?.zelle?.recipient_name}</p>
                </div>
                <div>
                  <Label className="text-white/80">Zelle confirmation #</Label>
                  <Input
                    value={zelleConfirmation}
                    onChange={(e) => setZelleConfirmation(e.target.value)}
                    placeholder="e.g., ZL-AB12CD34"
                    className="mt-1 bg-white/5 border-white/12 text-white placeholder:text-white/40 font-mono"
                    data-testid="submit-payment-confirmation-input"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Your name (Zelle sender)</Label>
                  <Input
                    value={zelleSenderName}
                    onChange={(e) => setZelleSenderName(e.target.value)}
                    placeholder="Name on your Zelle account"
                    className="mt-1 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                    data-testid="submit-payment-sender-input"
                  />
                </div>
                <p className="text-xs text-white/50">Admin will verify your Zelle transfer and activate within 24h.</p>
                <Button onClick={submitPayment} disabled={submitting} className="w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold" data-testid="submit-payment-button">
                  {submitting ? 'Submitting…' : 'Submit for review'}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
